import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Alert,
 AlertIcon,
 Box,
 Button,
 Flex,
 FormControl,
 FormLabel,
 Input,
 Modal,
 ModalBody,
 ModalCloseButton,
 ModalContent,
 ModalFooter,
 ModalHeader,
 ModalOverlay,
 Select,
 SkeletonText,
 Stack,
 Table,
 Tbody,
 Td,
 Text,
 Th,
 Thead,
 Tr,
} from '@chakra-ui/react';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import baseService from '../../services/base';
import { fetchData } from '../../services/fetch';

// EG-40: gestão do catálogo global de Temas.
//
// Tema não tem coluna de idioma — herda o do pilar via id_pillar. Por isso o
// pilar é obrigatório: sem ele o tema não pertence a nada, e a unicidade
// (pilar, nome) não teria como valer.

const FORMULARIO_VAZIO = { title: '', id_pillar: '' };

const normaliza = (texto) =>
 (texto || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .trim()
  .toLowerCase();

const mensagemDoErro = (erro) =>
 erro?.response?.data?.message ||
 'Não foi possível concluir a operação. Tente novamente.';

const Themes = () => {
 const { data: temas, isLoading, mutate } = useSWR('/theme', fetchData);
 const { data: pilares } = useSWR('/pillar', fetchData);

 const [busca, setBusca] = useState('');
 const [filtroPilar, setFiltroPilar] = useState('');
 const [erro, setErro] = useState('');
 const [formulario, setFormulario] = useState(null);
 const [emExclusao, setEmExclusao] = useState(null);
 // Sem retorno visual, o gestor clica e a tela fica parada até o modal fechar.
 const [gravando, setGravando] = useState(false);

 const lista = useMemo(() => {
  const alvo = normaliza(busca);
  return (temas || []).filter((tema) => {
   const casaNome = alvo ? normaliza(tema.title).includes(alvo) : true;
   const casaPilar = filtroPilar
    ? String(tema.id_pillar) === String(filtroPilar)
    : true;
   return casaNome && casaPilar;
  });
 }, [temas, busca, filtroPilar]);

 const salvar = async () => {
  if (gravando) return;

  if (!formulario?.title?.trim()) {
   setErro('O nome do tema é obrigatório.');
   return;
  }
  if (!formulario?.id_pillar) {
   setErro('Selecione o pilar ao qual o tema pertence.');
   return;
  }

  setErro('');
  setGravando(true);
  const dados = {
   title: formulario.title,
   id_pillar: Number(formulario.id_pillar),
  };

  try {
   if (formulario.id_theme) {
    await baseService.patch(`/theme/${formulario.id_theme}`, dados);
   } else {
    await baseService.post('/theme', dados);
   }
   setFormulario(null);
   mutate();
  } catch (falha) {
   setErro(mensagemDoErro(falha));
  } finally {
   setGravando(false);
  }
 };

 const excluir = async () => {
  if (gravando) return;

  setErro('');
  setGravando(true);
  try {
   await baseService.delete(`/theme/${emExclusao.id_theme}`);
   setEmExclusao(null);
   mutate();
  } catch (falha) {
   setErro(mensagemDoErro(falha));
  } finally {
   setGravando(false);
  }
 };

 const campo = (chave, valor) =>
  setFormulario((atual) => ({ ...atual, [chave]: valor }));

 return (
  <MainContent>
   <PageHeaderStructure
    title='Temas'
    subtitle='Catálogo global de temas. Cada tema pertence a um pilar e fica disponível para todos os questionários.'
   >
    <Button onClick={() => setFormulario({ ...FORMULARIO_VAZIO })}>
     Novo Tema
    </Button>
   </PageHeaderStructure>

   {erro && !formulario && !emExclusao && (
    <Alert status='error' mt='16px' borderRadius='12px'>
     <AlertIcon />
     {erro}
    </Alert>
   )}

   <Box bg='white' borderRadius='12px' p='24px' mt='24px'>
    <Flex gap='16px' mb='16px' wrap='wrap'>
     <Input
      placeholder='Buscar por nome'
      value={busca}
      maxW='320px'
      onChange={(evento) => setBusca(evento.target.value)}
     />
     <FormControl maxW='260px'>
      <FormLabel htmlFor='filtro-pilar' srOnly>
       Filtrar por pilar
      </FormLabel>
      <Select
       id='filtro-pilar'
       aria-label='Filtrar por pilar'
       placeholder='Todos os pilares'
       value={filtroPilar}
       onChange={(evento) => setFiltroPilar(evento.target.value)}
      >
       {(pilares || []).map((pilar) => (
        <option key={pilar.id_pillar} value={pilar.id_pillar}>
         {pilar.name_pillar}
        </option>
       ))}
      </Select>
     </FormControl>
    </Flex>

    {isLoading ? (
     <SkeletonText noOfLines={4} spacing='4' />
    ) : (
     <Table size='sm'>
      <Thead>
       <Tr>
        <Th>Nome</Th>
        <Th>Pilar vinculado</Th>
        <Th>Ações</Th>
       </Tr>
      </Thead>
      <Tbody>
       {lista.map((tema) => (
        <Tr key={tema.id_theme}>
         <Td>{tema.title}</Td>
         <Td>{tema.name_pillar}</Td>
         <Td>
          <Flex gap='8px'>
           <Button
            size='xs'
            variant='ghost'
            onClick={() => setFormulario({ ...tema })}
           >
            Editar
           </Button>
           <Button
            size='xs'
            variant='ghost'
            colorScheme='red'
            onClick={() => setEmExclusao(tema)}
           >
            Excluir
           </Button>
          </Flex>
         </Td>
        </Tr>
       ))}
      </Tbody>
     </Table>
    )}
   </Box>

   <Modal isOpen={!!formulario} onClose={() => setFormulario(null)}>
    <ModalOverlay />
    <ModalContent>
     <ModalHeader>
      {formulario?.id_theme ? 'Editar tema' : 'Novo tema'}
     </ModalHeader>
     <ModalCloseButton />
     <ModalBody>
      {erro && (
       <Alert status='error' mb='16px' borderRadius='8px'>
        <AlertIcon />
        {erro}
       </Alert>
      )}
      <Stack spacing='12px'>
       <FormControl isRequired>
        <FormLabel htmlFor='title'>Nome</FormLabel>
        <Input
         id='title'
         value={formulario?.title || ''}
         onChange={(e) => campo('title', e.target.value)}
        />
       </FormControl>
       <FormControl isRequired>
        <FormLabel htmlFor='id_pillar'>Pilar</FormLabel>
        <Select
         id='id_pillar'
         placeholder='Selecione o pilar'
         value={formulario?.id_pillar || ''}
         onChange={(e) => campo('id_pillar', e.target.value)}
        >
         {(pilares || []).map((pilar) => (
          <option key={pilar.id_pillar} value={pilar.id_pillar}>
           {pilar.name_pillar} ({pilar.language})
          </option>
         ))}
        </Select>
       </FormControl>
      </Stack>
     </ModalBody>
     <ModalFooter gap='12px'>
      <Button
       variant='ghost'
       isDisabled={gravando}
       onClick={() => setFormulario(null)}
      >
       Cancelar
      </Button>
      <Button isLoading={gravando} loadingText='Salvando' onClick={salvar}>
       Salvar
      </Button>
     </ModalFooter>
    </ModalContent>
   </Modal>

   <Modal isOpen={!!emExclusao} onClose={() => setEmExclusao(null)}>
    <ModalOverlay />
    <ModalContent>
     <ModalHeader>Excluir tema</ModalHeader>
     <ModalCloseButton />
     <ModalBody>
      {erro && (
       <Alert status='error' mb='16px' borderRadius='8px'>
        <AlertIcon />
        {erro}
       </Alert>
      )}
      <Text>
       O tema sai da seleção, mas as respostas e feedbacks já coletados
       continuam apontando para ele.
      </Text>
     </ModalBody>
     <ModalFooter gap='12px'>
      <Button variant='ghost' onClick={() => setEmExclusao(null)}>
       Cancelar
      </Button>
      <Button
       colorScheme='red'
       isLoading={gravando}
       loadingText='Excluindo'
       onClick={excluir}
      >
       Confirmar
      </Button>
     </ModalFooter>
    </ModalContent>
   </Modal>
  </MainContent>
 );
};

export default Themes;
